/**
 * Portal Make Payment (PD-14057) — pay one or more invoices in a single
 * payment, deliberately place money on account, and spend Account Funds.
 *
 * Mirrors the CRM Make Payment reconciliation model: the Total Payment is
 * either DERIVED from the applied amounts (default) or ENTERED as an explicit
 * constraint — in entered mode any unapplied remainder must be explicitly
 * routed to the customer account before submitting (no silent on-account
 * money). Account Funds (when the tenant has it configured and a balance
 * exists) is capped at the available amount and can never route money back
 * onto the account.
 */

import {
	faCreditCard,
	faPlus,
	faWallet,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalMakePaymentContextDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import {
	extractPaymentDecline,
	PaymentDeclineModal,
	Sidebar,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
	HistoryFilters,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { InvoiceDueChip } from '../components/InvoiceStatusChips';
import { PayAccountsView } from '../components/PayAccountsView';
import { PortalAddPaymentMethod } from '../components/PortalAddPaymentMethod';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { triggerLightImpact } from '../lib/haptics';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalPaymentService from '../services/portalPaymentService';
import { useLoginModalStore } from '../stores/loginModalStore';

const ACCOUNT_FUNDS = 'Account Funds';

interface LineStateModel {
	checked: boolean;
	amount: string;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

function parseAmount(raw: string): number {
	const parsed = parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

const SingleAccountMakePayment: React.FC = () => {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const navigate = useNavigate();
	const openLoginModal = useLoginModalStore((s) => s.open);

	// Signed-out visitors get the login modal over an empty page rather than
	// the payment form, matching the sibling account pages (PD-14185).
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const [context, setContext] = useState<PortalMakePaymentContextDto | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [lines, setLines] = useState<Record<number, LineStateModel>>({});
	/** Raw user-typed Total Payment. '' = derived mode. */
	const [totalInput, setTotalInput] = useState('');
	/** Explicit money placed on the customer account. */
	const [onAccount, setOnAccount] = useState('');
	const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [showAddPayment, setShowAddPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	// PD-15185 — a gateway decline, shown as a dialog rather than inline.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);
	const [success, setSuccess] = useState(false);

	// PD-14172 — open-invoices list filters (client-side; the full list is
	// already loaded into `context.openInvoices`). Credit memos are NOT shown
	// here — only payable open invoices — so there is no Invoice/Credit-Memo
	// Type filter on this page (that lives on the Invoices list page).
	// Filters live in the same Apply/Cancel tray as the Invoices list
	// (PD-14701): a relative date range (defaulting to ALL TIME — hiding an
	// old unpaid invoice by default would hide exactly what most needs
	// paying) plus an Overdue-only toggle.
	const [range, setRange] = useState('');
	// Explicit bounds for the 'custom' range (PD-14899); only meaningful —
	// and only retained — while `range` is 'custom'.
	const [rangeFrom, setRangeFrom] = useState('');
	const [rangeTo, setRangeTo] = useState('');
	const [overdueOnly, setOverdueOnly] = useState(false);
	const selectAllRef = useRef<HTMLInputElement>(null);
	/** Saved-method ids seen on the last fetch — a refetch that surfaces a
	 *  new id means the user just added that method (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	const fetchContext = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		setLoading(true);
		setLoadError(null);
		try {
			const res = await portalPaymentService.getMakePaymentContext(token);
			if (res.success) {
				setContext(res.data);
				// PD-15014 — MERGE line state instead of re-initializing it.
				// Adding a payment method mid-flow refetches this context, and a
				// full reset silently wiped every ticked invoice and typed amount.
				setLines((prev) => {
					const next: Record<number, LineStateModel> = {};
					for (const invoice of res.data.openInvoices) {
						next[invoice.id] = prev[invoice.id] ?? {
							checked: false,
							amount: '',
						};
					}
					return next;
				});
				const methods = res.data.savedPaymentMethods;
				const types = res.data.paymentTypes;
				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				if (prevKnown == null) {
					// First load — auto-select the first NON-Account-Funds type;
					// spending the customer's balance must always be an explicit
					// choice.
					const firstType = types.find(
						(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS,
					);
					if (firstType != null) {
						setSelectedTypeId(firstType.id);
						const forType = methods.filter(
							(m) => m.type === firstType.paymentMethodType,
						);
						const primary = forType.find((m) => m.isPrimary);
						setSelectedMethodId(
							primary?.id ?? (forType.length > 0 ? forType[0].id : null),
						);
					}
				} else {
					// Refetch (add-payment-method sidebar closed). A method id we
					// have not seen before was just added — select it and its type,
					// since paying with it is why it was added. Otherwise leave the
					// user's selection untouched.
					const fresh = methods.find((m) => !prevKnown.has(m.id));
					if (fresh != null) {
						const freshType = types.find(
							(pt) => pt.paymentMethodType === fresh.type,
						);
						if (freshType != null) {
							setSelectedTypeId(freshType.id);
						}
						setSelectedMethodId(fresh.id);
					}
				}
			}
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : t('Failed to load payment data'),
			);
		} finally {
			setLoading(false);
		}
	}, [token, t]);

	useEffect(() => {
		void fetchContext();
	}, [fetchContext]);

	const selectedType: SupportedPaymentMethodType | undefined =
		context?.paymentTypes.find((pt) => pt.id === selectedTypeId);
	const isAccountFundsMethod =
		selectedType?.paymentMethodType === ACCOUNT_FUNDS;
	const availableFunds = context?.accountFunds.available ?? 0;
	// PD-14922 — whether money may be ADDED to the account. Off for tenants
	// whose ERP cannot record an on-account deposit. Independent of whether
	// Account Funds is usable as a payment method: the customer keeps spending
	// the balance they already hold. Defaults to allowed before the context
	// loads, so the row does not flash out from under a mid-typing customer.
	const depositAllowed = context?.accountFunds.depositAllowed ?? true;

	const methodsForType = (context?.savedPaymentMethods ?? []).filter(
		(m) => m.type === selectedType?.paymentMethodType,
	);

	// PD-15014 — fee preview inputs. Only a surchargeable card carries the
	// fee (PD-15173); the rate itself is the server-resolved effective fee.
	const selectedMethod = methodsForType.find((m) => m.id === selectedMethodId);
	const feeApplies = selectedMethod == null || selectedMethod.isSurchargeable;

	// ─── Reconciliation math ─────────────────────────────────────────
	// PD-15441 — credit memos (RMAs) arrive on the same context as credit
	// rows (`isCreditMemo`). Their applied amounts SUBTRACT: the customer
	// pays the net of the selected invoices less the credit applied.
	const invoiceRows = useMemo(
		() =>
			(context?.openInvoices ?? []).filter(
				(invoice) => invoice.isCreditMemo !== true,
			),
		[context?.openInvoices],
	);
	const creditMemoRows = useMemo(
		() =>
			(context?.openInvoices ?? []).filter(
				(invoice) => invoice.isCreditMemo === true,
			),
		[context?.openInvoices],
	);

	const appliedTotal = useMemo(
		() =>
			round2(
				invoiceRows.reduce((sum, invoice) => {
					const line = lines[invoice.id] as LineStateModel | undefined;
					return sum + (line?.checked === true ? parseAmount(line.amount) : 0);
				}, 0),
			),
		[invoiceRows, lines],
	);
	/** Credit applied from checked credit memo rows, as a positive magnitude. */
	const creditApplied = useMemo(
		() =>
			round2(
				creditMemoRows.reduce((sum, memo) => {
					const line = lines[memo.id] as LineStateModel | undefined;
					return sum + (line?.checked === true ? parseAmount(line.amount) : 0);
				}, 0),
			),
		[creditMemoRows, lines],
	);
	const anyCreditChecked = useMemo(
		() =>
			creditMemoRows.some(
				(memo) =>
					(lines[memo.id] as LineStateModel | undefined)?.checked === true,
			),
		[creditMemoRows, lines],
	);
	const availableCredit = useMemo(
		() =>
			round2(
				creditMemoRows.reduce((sum, memo) => sum + memo.outstandingBalance, 0),
			),
		[creditMemoRows],
	);
	const onAccountAmount = parseAmount(onAccount);
	const derivedTotal = round2(appliedTotal - creditApplied + onAccountAmount);
	const enteredTotal = totalInput === '' ? null : parseAmount(totalInput);
	const effectiveTotal = enteredTotal ?? derivedTotal;
	/** Entered-mode remainder that has no destination yet. */
	const unapplied =
		enteredTotal != null ? round2(enteredTotal - derivedTotal) : 0;
	/** Applied credit may never exceed the invoice amounts it offsets. */
	const creditExceedsApplied = round2(appliedTotal - creditApplied) < -0.004;
	/** Server refuses mixing credit applications with an on-account deposit. */
	const creditWithDeposit = creditApplied > 0 && onAccountAmount > 0;

	// PD-15014 — the processing fee this payment will actually add, floored
	// per transaction to match the server's charge (PD-15174). Zero when the
	// selected card cannot be surcharged or Account Funds is paying.
	// PD-15441 — with credit applied, mirror the server's sizing exactly:
	// each invoice line carries its own floored fee and the credit relieves
	// the added fee (floored, capped) — a pooled floor over the net can read
	// a cent under what the card is actually charged.
	const feeAmount = useMemo(() => {
		if (
			selectedType == null ||
			isAccountFundsMethod ||
			!feeApplies ||
			effectiveTotal <= 0
		) {
			return 0;
		}
		if (selectedType.processingFeeType !== '%') {
			return selectedType.processingFee;
		}
		if (creditApplied > 0) {
			const newFees = invoiceRows.reduce((sum, invoice) => {
				const line = lines[invoice.id] as LineStateModel | undefined;
				if (line == null || !line.checked) {
					return sum;
				}
				return (
					sum +
					Math.floor(parseAmount(line.amount) * selectedType.processingFee) /
						100
				);
			}, 0);
			const relief = Math.min(
				Math.floor(creditApplied * selectedType.processingFee) / 100,
				newFees,
			);
			return round2(newFees - relief);
		}
		return Math.floor(effectiveTotal * selectedType.processingFee) / 100;
	}, [
		selectedType,
		isAccountFundsMethod,
		feeApplies,
		effectiveTotal,
		creditApplied,
		invoiceRows,
		lines,
	]);
	const totalWithFee = round2(effectiveTotal + feeAmount);

	const accountFundsExceeded =
		isAccountFundsMethod && effectiveTotal > availableFunds + 0.005;
	const accountFundsSelfDeposit = isAccountFundsMethod && onAccountAmount > 0;
	const overApplied = unapplied < -0.005;
	const hasUnapplied = unapplied > 0.005;

	const lineErrors = useMemo(() => {
		const errors: Record<number, string> = {};
		for (const invoice of invoiceRows) {
			const line = lines[invoice.id] as LineStateModel | undefined;
			if (line == null || !line.checked) {
				continue;
			}
			const amount = parseAmount(line.amount);
			if (amount > invoice.outstandingBalance + 0.005) {
				errors[invoice.id] = t('Exceeds outstanding balance');
			}
		}
		for (const memo of creditMemoRows) {
			const line = lines[memo.id] as LineStateModel | undefined;
			if (line == null || !line.checked) {
				continue;
			}
			const amount = parseAmount(line.amount);
			if (amount > memo.outstandingBalance + 0.005) {
				errors[memo.id] = t('Exceeds remaining credit');
			}
		}
		return errors;
	}, [invoiceRows, creditMemoRows, lines, t]);

	// ─── Open-invoices filtering + select-all (PD-14172) ─────────────
	const hasOpenInvoices = invoiceRows.length > 0;
	// Date columns come back as `YYYY-MM-DD` (or an ISO timestamp); compare on
	// the date portion only, lexicographically — valid for ISO date strings.
	const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

	const filteredInvoices = useMemo(() => {
		// '' = the page default (all time); resolveHistoryRange('all') → no
		// bounds. Any explicit pick resolves to inclusive ISO date bounds.
		const bounds = resolveHistoryRange(
			range === '' ? 'all' : range,
			rangeFrom,
			rangeTo,
		);
		const dateFrom = bounds.dateFrom ?? '';
		const dateTo = bounds.dateTo ?? '';
		return invoiceRows.filter((invoice) => {
			const invDate = invoice.date?.slice(0, 10) ?? null;
			if (dateFrom !== '' && (invDate == null || invDate < dateFrom)) {
				return false;
			}
			if (dateTo !== '' && (invDate == null || invDate > dateTo)) {
				return false;
			}
			if (overdueOnly) {
				const due = invoice.dueDate?.slice(0, 10) ?? null;
				if (due == null || due >= todayIso) {
					return false;
				}
			}
			return true;
		});
	}, [invoiceRows, range, rangeFrom, rangeTo, overdueOnly, todayIso]);

	const filtersActive = (range !== '' && range !== 'all') || overdueOnly;

	// Set of currently-checked invoice ids — avoids indexing `lines` per row
	// and is robust if a line entry is ever missing.
	const checkedIds = useMemo(() => {
		const ids = new Set<number>();
		for (const [id, line] of Object.entries(lines)) {
			if (line.checked) {
				ids.add(Number(id));
			}
		}
		return ids;
	}, [lines]);

	const allFilteredChecked =
		filteredInvoices.length > 0 &&
		filteredInvoices.every((inv) => checkedIds.has(inv.id));
	const someFilteredChecked = filteredInvoices.some((inv) =>
		checkedIds.has(inv.id),
	);

	// Native checkboxes have no React-controlled "indeterminate" — set it
	// imperatively for the partial-selection state.
	useEffect(() => {
		if (selectAllRef.current != null) {
			selectAllRef.current.indeterminate =
				someFilteredChecked && !allFilteredChecked;
		}
	}, [someFilteredChecked, allFilteredChecked]);

	const needsSavedMethod = !isAccountFundsMethod;
	const canSubmit =
		!submitting &&
		// PD-15441 — $0 is submittable when applied credit fully covers the
		// selection (record-the-applications payment). Negative nets and
		// credit-only selections stay blocked.
		effectiveTotal >= 0 &&
		(effectiveTotal > 0 || creditApplied > 0) &&
		!creditExceedsApplied &&
		!creditWithDeposit &&
		!hasUnapplied &&
		!overApplied &&
		Object.keys(lineErrors).length === 0 &&
		!accountFundsExceeded &&
		!accountFundsSelfDeposit &&
		selectedTypeId != null &&
		(!needsSavedMethod ? true : selectedMethodId != null);

	// ─── Handlers ────────────────────────────────────────────────────
	const toggleLine = (invoiceId: number, outstanding: number): void => {
		setLines((prev) => {
			const current = prev[invoiceId] ?? { checked: false, amount: '' };
			const checked = !current.checked;
			return {
				...prev,
				[invoiceId]: {
					checked,
					amount: checked ? outstanding.toFixed(2) : '',
				},
			};
		});
		setSubmitError(null);
	};

	/**
	 * PD-15441 — toggling a credit memo row. Credit application always runs
	 * in derived mode (the net total follows the rows), and the fill is
	 * capped at what the currently selected invoices can absorb, so checking
	 * a credit never nets below zero on its own.
	 */
	const toggleCreditMemo = (memoId: number, remainingCredit: number): void => {
		setTotalInput('');
		setLines((prev) => {
			const current = prev[memoId] ?? { checked: false, amount: '' };
			if (current.checked) {
				return {
					...prev,
					[memoId]: { checked: false, amount: '' },
				};
			}
			const selectedInvoiceSum = invoiceRows.reduce((sum, invoice) => {
				const line = prev[invoice.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const otherCreditsApplied = creditMemoRows.reduce((sum, other) => {
				if (other.id === memoId) {
					return sum;
				}
				const line = prev[other.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const absorbable = Math.max(
				0,
				round2(selectedInvoiceSum - otherCreditsApplied),
			);
			const fill = Math.min(remainingCredit, absorbable);
			return {
				...prev,
				[memoId]: {
					checked: true,
					amount: fill > 0 ? fill.toFixed(2) : '',
				},
			};
		});
		setSubmitError(null);
	};

	// Select-all toggles only the currently-visible (filtered) rows, defaulting
	// each to its full outstanding balance — matching a single-row check.
	const toggleSelectAll = (): void => {
		const shouldCheck = !allFilteredChecked;
		setLines((prev) => {
			const next = { ...prev };
			for (const invoice of filteredInvoices) {
				next[invoice.id] = shouldCheck
					? { checked: true, amount: invoice.outstandingBalance.toFixed(2) }
					: { checked: false, amount: '' };
			}
			return next;
		});
		setSubmitError(null);
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		setLines((prev) => ({
			...prev,
			[invoiceId]: { checked: true, amount },
		}));
		setSubmitError(null);
	};

	const blurCurrency = (raw: string): string => {
		const parsed = parseFloat(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(2) : '';
	};

	const applyUnappliedToAccount = (): void => {
		setOnAccount(round2(onAccountAmount + unapplied).toFixed(2));
	};

	const handleSelectType = (typeId: number): void => {
		setSelectedTypeId(typeId);
		setSubmitError(null);
		const type = context?.paymentTypes.find((pt) => pt.id === typeId);
		const methods = (context?.savedPaymentMethods ?? []).filter(
			(m) => m.type === type?.paymentMethodType,
		);
		const primary = methods.find((m) => m.isPrimary);
		setSelectedMethodId(
			primary?.id ?? (methods.length > 0 ? methods[0].id : null),
		);
	};

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit || context == null) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		try {
			const allocations = [
				...invoiceRows
					.filter((invoice) => {
						const line = lines[invoice.id] as LineStateModel | undefined;
						return line != null && line.checked && parseAmount(line.amount) > 0;
					})
					.map((invoice) => ({
						invoiceId: invoice.id,
						amount: parseAmount(lines[invoice.id].amount),
					})),
				// PD-15441 — credit memo applications go up NEGATIVE: the memo's
				// balance offsets the invoices and only the net is charged.
				...creditMemoRows
					.filter((memo) => {
						const line = lines[memo.id] as LineStateModel | undefined;
						return line != null && line.checked && parseAmount(line.amount) > 0;
					})
					.map((memo) => ({
						invoiceId: memo.id,
						amount: -parseAmount(lines[memo.id].amount),
					})),
			];

			const res = await portalPaymentService.makePayment(token, {
				paymentAmount: effectiveTotal,
				invoiceAllocations: allocations,
				onAccountAmount,
				...(isAccountFundsMethod
					? { paymentMethodType: ACCOUNT_FUNDS }
					: { paymentMethodId: selectedMethodId ?? undefined }),
			});
			if (res.success) {
				// PD-14695: a light tap confirms the payment landed.
				triggerLightImpact();
				setSuccess(true);
				window.setTimeout(() => {
					void navigate('/invoices');
				}, 1800);
			}
		} catch (err) {
			// PD-15185 — a refusal from the gateway is shown as a dialog the
			// customer has to acknowledge, with the reason, rather than a line
			// of text they may not notice above the button they just pressed.
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				setSubmitError(
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.'),
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Render ──────────────────────────────────────────────────────
	if (!isAuthenticated) {
		return <div />;
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-16">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (loadError != null) {
		return (
			<div className="p-4">
				<div className="p-4 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(loadError)}</p>
				</div>
			</div>
		);
	}

	if (success) {
		return (
			<div className="flex flex-col items-center justify-center py-16 gap-3">
				<p className="text-lg font-bold text-default">
					{t('Payment submitted')}
				</p>
				<p className="text-sm text-brand-muted">
					{t('Returning to your invoices...')}
				</p>
			</div>
		);
	}

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<h1 className="text-lg md:text-xl font-bold text-default mb-1">
				{t('Make a Payment')}
			</h1>
			<p className="text-sm text-brand-muted mb-4">
				{t('Select invoices to pay, or add money to your account.')}
			</p>

			{/* No "Account funds available" banner here — the balance already
			    shows on the Account Funds option in Payment Method below;
			    saying it twice on one screen was noise. */}

			{/* Open invoices */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Open Invoices')}
				</h2>

				{!hasOpenInvoices ? (
					<p className="text-sm text-brand-muted">
						{t('No open invoices. You can still add money to your account.')}
					</p>
				) : (
					<>
						{/*
						  Filters (PD-14172, tray rework per PD-14701) — one row:
						  the unlabeled select-all (bulk selector) and the same
						  Apply/Cancel filter tray as the Invoices list, holding
						  the relative date range (default: all time) and the
						  Overdue-only toggle.
						*/}
						<div className="flex items-center gap-3 mb-3">
							{filteredInvoices.length > 0 && (
								<Tooltip content={t('Select all')} position="top">
									<label className="flex items-center min-h-touch cursor-pointer">
										<input
											ref={selectAllRef}
											type="checkbox"
											aria-label={t('Select all')}
											checked={allFilteredChecked}
											onChange={toggleSelectAll}
											className="w-4 h-4 text-portal-accent"
										/>
									</label>
								</Tooltip>
							)}
							<div className="flex-1 min-w-0">
								<HistoryFilters
									fields={[
										{
											kind: 'range',
											key: 'range',
											label: 'Date Range',
											defaultRange: 'all',
										},
										{ kind: 'toggle', key: 'overdue', label: 'Overdue only' },
									]}
									values={{
										range,
										from: rangeFrom,
										to: rangeTo,
										overdue: overdueOnly ? '1' : '',
									}}
									onApply={(next) => {
										// Both keys are always present: the tray drafts from
										// `values` above and Clear-all writes '' per field.
										// A custom pick with both bounds blank is not a
										// filter — collapse it to the page default (PD-14899).
										const hasCustomBounds =
											next.range === 'custom' &&
											(next.from !== '' || next.to !== '');
										setRange(
											next.range === 'all' ||
												(next.range === 'custom' && !hasCustomBounds)
												? ''
												: next.range,
										);
										setRangeFrom(hasCustomBounds ? next.from : '');
										setRangeTo(hasCustomBounds ? next.to : '');
										setOverdueOnly(next.overdue === '1');
									}}
								/>
							</div>
						</div>

						{filteredInvoices.length === 0 ? (
							<div className="text-sm text-brand-muted">
								<p>{t('No invoices match your filters.')}</p>
								{filtersActive && (
									<button
										type="button"
										onClick={() => {
											setRange('');
											setRangeFrom('');
											setRangeTo('');
											setOverdueOnly(false);
										}}
										className="mt-1 text-xs font-medium text-portal-accent hover:underline"
									>
										{t('Clear filters')}
									</button>
								)}
							</div>
						) : (
							<div className="max-h-96 overflow-y-auto overflow-x-hidden space-y-2">
								{filteredInvoices.map((invoice) => {
									const line = lines[invoice.id] ?? {
										checked: false,
										amount: '',
									};
									return (
										<div
											key={invoice.id}
											className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
										>
											<input
												type="checkbox"
												checked={line.checked}
												onChange={() => {
													toggleLine(invoice.id, invoice.outstandingBalance);
												}}
												className="w-4 h-4 text-portal-accent"
											/>
											<div className="flex-1 min-w-[120px]">
												<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
													<p className="text-sm font-medium text-default">
														{t('Invoice')} #{t(invoice.invoiceNumber)}
													</p>
													{/* Same urgency chip as the Invoices list
													    (PD-14697). Open invoices have no `total`
													    here; passing null reads as partial, which
													    the due chip treats the same as unpaid. */}
													<InvoiceDueChip
														invoice={{
															outstandingBalance: String(
																invoice.outstandingBalance,
															),
															total: null,
															dueDate: invoice.dueDate ?? null,
														}}
													/>
												</div>
												<p className="text-xs text-brand-muted">
													{t('Outstanding')}:{' '}
													{t(formatCurrency(invoice.outstandingBalance) ?? '')}
													{invoice.date != null && (
														<>
															{' · '}
															{t('Dated')}{' '}
															{t(formatStatementDate(invoice.date))}
														</>
													)}
												</p>
											</div>
											<div className="relative w-32">
												<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
													$
												</span>
												<input
													type="number"
													value={line.amount}
													disabled={!line.checked}
													min="0.01"
													step="0.01"
													onChange={(e) => {
														setLineAmount(invoice.id, e.target.value);
													}}
													onBlur={() => {
														setLineAmount(
															invoice.id,
															blurCurrency(line.amount),
														);
													}}
													placeholder="0.00"
													className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-70"
												/>
											</div>
											{(lineErrors[invoice.id] as string | undefined) !=
												null && (
												<p className="w-full text-xs text-danger text-right">
													{t(lineErrors[invoice.id])}
												</p>
											)}
										</div>
									);
								})}
							</div>
						)}
					</>
				)}

				{/* PD-15441 — credit memos (RMAs): applied credit reduces what is
				    charged for the invoices above. Rows only arrive when the
				    tenant has payments.pay_invoices_with_credit_memos enabled. */}
				{creditMemoRows.length > 0 && (
					<div className="mt-3 border-t border-brand-border pt-3">
						<div className="flex items-center justify-between mb-1">
							<h3 className="text-sm font-semibold text-default">
								{t('Credit Memos')}
							</h3>
							<p className="text-xs text-brand-muted">
								{t('Available credit')}:{' '}
								{t(formatCurrency(availableCredit) ?? '')}
							</p>
						</div>
						<p className="text-xs text-brand-muted mb-2">
							{t(
								'Applied credit reduces the amount charged for the invoices above.',
							)}
						</p>
						<div className="space-y-2">
							{creditMemoRows.map((memo) => {
								const line = lines[memo.id] ?? {
									checked: false,
									amount: '',
								};
								return (
									<div
										key={memo.id}
										className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
									>
										<input
											type="checkbox"
											checked={line.checked}
											onChange={() => {
												toggleCreditMemo(memo.id, memo.outstandingBalance);
											}}
											className="w-4 h-4 text-portal-accent"
										/>
										<div className="flex-1 min-w-[120px]">
											<p className="text-sm font-medium text-default">
												{t('Credit Memo')} #{t(memo.invoiceNumber)}
											</p>
											<p className="text-xs text-brand-muted">
												{t('Remaining credit')}:{' '}
												{t(formatCurrency(memo.outstandingBalance) ?? '')}
												{memo.date != null && (
													<>
														{' · '}
														{t('Dated')} {t(formatStatementDate(memo.date))}
													</>
												)}
											</p>
										</div>
										<div className="relative w-32">
											<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
												-$
											</span>
											<input
												type="number"
												value={line.amount}
												disabled={!line.checked}
												min="0.01"
												step="0.01"
												onChange={(e) => {
													setLineAmount(memo.id, e.target.value);
												}}
												onBlur={() => {
													setLineAmount(memo.id, blurCurrency(line.amount));
												}}
												placeholder="0.00"
												className="w-full pl-8 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-70"
											/>
										</div>
										{(lineErrors[memo.id] as string | undefined) != null && (
											<p className="w-full text-xs text-danger text-right">
												{t(lineErrors[memo.id])}
											</p>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* On-account row — hidden when the tenant does not allow adding
				    money to the account (PD-14922). Spending existing funds is
				    unaffected: Account Funds stays selectable below. */}
				{depositAllowed && (
					<div className="flex flex-wrap items-center gap-3 p-2 -mx-2 mt-3 border-t border-brand-border pt-3">
						<div className="flex-1 min-w-[120px]">
							<p className="text-sm font-medium text-default">
								{t('Apply to My Account')}
							</p>
							<p className="text-xs text-brand-muted">
								{t(
									'Funds added to your account balance instead of an invoice.',
								)}
							</p>
						</div>
						<div className="relative w-32">
							<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
								$
							</span>
							<input
								type="number"
								value={onAccount}
								min="0"
								step="0.01"
								disabled={isAccountFundsMethod}
								onChange={(e) => {
									setOnAccount(e.target.value);
									setSubmitError(null);
								}}
								onBlur={() => {
									setOnAccount(blurCurrency(onAccount));
								}}
								placeholder="0.00"
								className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-70"
							/>
						</div>
					</div>
				)}
			</div>

			{/* Totals + reconciliation */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<div className="flex-1 min-w-[140px]">
						<label className="block text-sm font-medium text-strong">
							{t('Total Payment')}
						</label>
						<p className="text-xs text-brand-muted">
							{anyCreditChecked
								? t(
										'Calculated from the amounts above, less the applied credit.',
									)
								: enteredTotal == null
									? t('Calculated from the amounts above.')
									: t('Entered amount — applied amounts must match it.')}
						</p>
						{creditApplied > 0 && (
							<p className="text-xs text-portal-accent">
								{t('Includes {{amount}} of applied credit.', {
									amount: formatCurrency(creditApplied) ?? '',
								})}
							</p>
						)}
					</div>
					<div className="relative w-36">
						<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
							$
						</span>
						<input
							type="number"
							value={totalInput === '' ? derivedTotal.toFixed(2) : totalInput}
							min="0.01"
							step="0.01"
							disabled={anyCreditChecked}
							onChange={(e) => {
								setTotalInput(e.target.value);
								setSubmitError(null);
							}}
							onBlur={() => {
								setTotalInput(blurCurrency(totalInput));
							}}
							className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right font-semibold text-default disabled:opacity-70"
						/>
					</div>
					{enteredTotal != null && (
						<button
							type="button"
							onClick={() => {
								setTotalInput('');
							}}
							className="text-xs font-medium text-portal-accent hover:underline"
						>
							{t('Use calculated total')}
						</button>
					)}
				</div>

				{hasUnapplied && (
					<div className="mt-3 p-3 rounded-lg flex flex-wrap items-center gap-2 bg-warning-badge text-warning-tint-fg border border-warning-tint">
						<p className="text-sm text-warning flex-1">
							{t('{{amount}} of your payment is not applied to anything yet.', {
								amount: formatCurrency(unapplied) ?? '',
							})}
						</p>
						{!isAccountFundsMethod && depositAllowed && (
							<button
								type="button"
								onClick={applyUnappliedToAccount}
								className="text-xs font-medium px-3 py-1.5 bg-portal-accent text-white rounded-lg"
							>
								{t('Apply {{amount}} to my account', {
									amount: formatCurrency(unapplied) ?? '',
								})}
							</button>
						)}
					</div>
				)}
				{overApplied && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'The amounts above exceed the total payment. Reduce them or raise the total.',
						)}
					</p>
				)}
				{creditExceedsApplied && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
						)}
					</p>
				)}
				{creditWithDeposit && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'Credit memos cannot be combined with an Apply to My Account amount in the same payment.',
						)}
					</p>
				)}
			</div>

			{/* Payment method */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Payment Method')}
				</h2>
				<div className="space-y-2">
					{context?.paymentTypes.map((pt) => {
						const isAf = pt.paymentMethodType === ACCOUNT_FUNDS;
						const isSelected = selectedTypeId === pt.id;
						// PD-15014 — once a card is chosen the selected row says what
						// THAT card pays (PD-15173): a non-surchargeable card pays
						// nothing even on a fee-bearing type.
						const rowFee = isSelected && !feeApplies ? 0 : pt.processingFee;
						return (
							<React.Fragment key={pt.id}>
								<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
									<input
										type="radio"
										name="makePaymentType"
										checked={isSelected}
										onChange={() => {
											handleSelectType(pt.id);
										}}
										className="w-4 h-4 text-portal-accent"
									/>
									<FontAwesomeIcon
										icon={isAf ? faWallet : faCreditCard}
										className="text-default"
									/>
									<span className="text-sm text-default">
										{isAf
											? `${t('Account Funds')} — ${t(formatCurrency(availableFunds) ?? '')} ${t('available')}`
											: t(pt.paymentMethodType)}
									</span>
									{/* PD-15014 — label BOTH cases (Account Funds aside):
									    silence reads as "unknown", not "free", so a fee-free
									    type gets no credit for being cheaper. Mirrors the
									    individual-invoice sidebar. */}
									{!isAf && (
										<span
											className={`text-xs ml-auto ${
												rowFee > 0
													? 'text-brand-muted'
													: 'text-portal-accent font-medium'
											}`}
										>
											{rowFee > 0
												? pt.processingFeeType === '%'
													? `${rowFee}% ${t('fee')}`
													: `${formatCurrency(rowFee)} ${t('fee')}`
												: t('No fee')}
										</span>
									)}
								</label>
								{/* Saved methods render DIRECTLY under the selected
								    option (indented to its text column) — not after
								    the whole list, where picking ACH put the account
								    dropdown visually under Credit Card. */}
								{isSelected && !isAf && (
									<div className="pl-7 pb-1">
										{methodsForType.length > 0 ? (
											<select
												value={selectedMethodId ?? ''}
												onChange={(e) => {
													setSelectedMethodId(parseInt(e.target.value, 10));
												}}
												className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
											>
												{methodsForType.map((m) => (
													<option key={m.id} value={m.id}>
														{t(m.subtype ?? m.type)}{' '}
														{m.lastFour != null ? `****${m.lastFour}` : ''}{' '}
														{m.name != null ? `(${t(m.name)})` : ''}
													</option>
												))}
											</select>
										) : (
											<p className="text-xs text-brand-muted py-1">
												{t('No saved payment methods for this type.')}
											</p>
										)}
										<button
											type="button"
											onClick={() => {
												setShowAddPayment(true);
											}}
											className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-3"
										>
											<FontAwesomeIcon icon={faPlus} />
											<span>{t('Add new payment method')}</span>
										</button>
									</div>
								)}
							</React.Fragment>
						);
					})}
				</div>

				{accountFundsExceeded && (
					<p className="mt-2 text-sm text-danger">
						{t('Payment exceeds your available account funds of {{amount}}.', {
							amount: formatCurrency(availableFunds) ?? '',
						})}
					</p>
				)}
				{accountFundsSelfDeposit && (
					<p className="mt-2 text-sm text-danger">
						{t(
							'Account funds cannot be applied back to your account — clear the Apply to My Account amount.',
						)}
					</p>
				)}

				{/* PD-15014 — say what the card will actually be charged. Mirrors
				    the individual-invoice sidebar's fee preview. */}
				{feeAmount > 0 && (
					<div className="mt-3 p-3 bg-brand-input rounded-lg">
						<div className="flex justify-between text-sm">
							<span className="text-brand-muted">{t('Payment')}</span>
							<span className="text-default">
								{t(formatCurrency(effectiveTotal) ?? '')}
							</span>
						</div>
						<div className="flex justify-between text-sm mt-1">
							<span className="text-brand-muted">{t('Processing fee')}</span>
							<span className="text-default">
								{t(formatCurrency(feeAmount) ?? '')}
							</span>
						</div>
						<div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t border-brand-border">
							<span className="text-default">{t('Total charge')}</span>
							<span className="text-default">
								{t(formatCurrency(totalWithFee) ?? '')}
							</span>
						</div>
					</div>
				)}
			</div>

			{submitError != null && (
				<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(submitError)}</p>
				</div>
			)}

			<button
				type="button"
				onClick={() => void handleSubmit()}
				disabled={!canSubmit}
				className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-control-strong disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-6"
			>
				{submitting ? (
					<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
				) : null}
				{submitting
					? t('Processing...')
					: effectiveTotal <= 0 && creditApplied > 0
						? t('Apply Credit')
						: t('Pay {{amount}}', {
								amount:
									formatCurrency(effectiveTotal > 0 ? effectiveTotal : 0) ?? '',
							})}
			</button>

			{/* Add payment method sidebar */}
			<Sidebar
				panelClassName="bg-brand-surface"
				isOpen={showAddPayment}
				onClose={() => {
					setShowAddPayment(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => {
							setShowAddPayment(false);
							void fetchContext();
						}}
						onCancel={() => {
							setShowAddPayment(false);
						}}
						supportedPaymentMethods={(context?.paymentTypes ?? []).filter(
							(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS,
						)}
						preSelectedPaymentType={
							selectedType?.paymentMethodType === 'ACH'
								? PaymentMethodTypeEnum.ACH
								: selectedType?.paymentMethodType === 'Credit Card'
									? PaymentMethodTypeEnum.CREDIT_CARD
									: undefined
						}
					/>
				</div>
			</Sidebar>

			{/* PD-15185 — a refused payment, acknowledged deliberately. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</div>
	);
};

/**
 * Users who manage more than one account get the grouped, multi-account "pay
 * across my accounts" view (PD-14190); single-account users keep the original
 * flat flow.
 */
export const MakePaymentPage: React.FC = () => {
	const { availableClients } = usePortalAuth();
	return availableClients.length > 1 ? (
		<PayAccountsView />
	) : (
		<SingleAccountMakePayment />
	);
};

export default MakePaymentPage;
